import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Gift } from 'lucide-react';
import { plansAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';

const TIER_STYLE = {
  novo: 'bg-slate-100 text-slate-700',
  regular: 'bg-blue-100 text-blue-800',
  fiel: 'bg-amber-100 text-amber-800',
  premium: 'bg-purple-100 text-purple-800',
};

const rewardText = (r, t) => (
  r.reward_kind === 'percent' ? `-${r.reward_value}%`
    : r.reward_kind === 'fixed' ? `-${r.reward_value.toLocaleString('pt-PT')} CVE`
      : `+${r.reward_value} ${t('loyalty.bonusCredits')}`
);

const RuleRow = ({ r, t }) => {
  const pct = r.threshold ? Math.min(100, Math.round(((r.current || 0) / r.threshold) * 100)) : 100;
  const done = r.redeemed && !r.repeat;
  return (
    <li className="py-2" data-testid={`loyalty-rule-${r.id}`}>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className={done ? 'text-slate-400 line-through' : 'text-slate-800 font-medium'}>{r.name}</span>
        <span className="font-semibold text-emerald-700 whitespace-nowrap">{rewardText(r, t)}</span>
      </div>
      {r.trigger !== 'republish' && !done && (
        <>
          <div className="h-1.5 bg-slate-200 rounded-full mt-1.5 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {r.reached ? t('loyalty.readyNext') : t(`loyalty.progress.${r.trigger}`, { current: r.current ?? 0, threshold: r.threshold })}
          </p>
        </>
      )}
      {r.trigger === 'republish' && <p className="text-xs text-slate-500 mt-1">{t('loyalty.progress.republish')}</p>}
      {done && <p className="text-xs text-slate-400 mt-1">{t('loyalty.redeemed')}</p>}
    </li>
  );
};

export const LoyaltyCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => { plansAPI.getLoyalty().then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || !data.rules?.length) return null;
  return (
    <Card className="p-6 mb-8 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="loyalty-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-emerald-600" />
          <h3 className="font-semibold text-slate-900">{t('loyalty.title')}</h3>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${TIER_STYLE[data.tier]}`} data-testid="loyalty-tier">
          <Award className="h-3.5 w-3.5" />{t(`loyalty.tier.${data.tier}`)}
        </span>
      </div>
      <p className="text-xs text-slate-600 mb-2">
        {t('loyalty.summary', { purchases: data.purchases_count, spent: data.total_spent_cve.toLocaleString('pt-PT') })}
      </p>
      <ul className="divide-y divide-emerald-100">
        {data.rules.map((r) => <RuleRow key={r.id} r={r} t={t} />)}
      </ul>
    </Card>
  );
};
